import { useEffect, useMemo, useState } from 'react';

/**
 * Stream Starting — 1920×1080 fullscreen pre-stream scene.
 *
 * Cinematic asymmetric layout: oversized Bebas-Neue display text
 * anchored top-left, massive countdown digits, diagonal accent slash
 * crossing the right half. Pairs with OBS's "Starting Soon" scene —
 * streamer drops their own audio on top.
 *
 * Config:
 *   targetDate: ISO string                    (required)
 *   title: string                             (default 'Stream starts in')
 *   streamTitle: string                       (default 'Welcome back to the stream')
 *   startedMessage: string                    (default 'Going live now…')
 *   accentColor: string                       (default '#a855f7')
 *   secondaryColor: string                    (default '#06b6d4')
 *   backgroundUrl: string                     (optional — overrides gradient mesh)
 *   showSocials: boolean                      (default true)
 *   twitchHandle: string                      (default '' — empty hides the chip)
 *   youtubeHandle: string                     (default '')
 *   kickHandle: string                        (default '')
 *   showScanlines: boolean                    (default true)
 */

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

interface Segments {
  d: number;
  h: number;
  m: number;
  s: number;
  finished: boolean;
}

function computeSegments(target: number, now: number): Segments {
  const diff = Math.max(0, target - now);
  const totalSecs = Math.floor(diff / 1000);
  const d = Math.floor(totalSecs / 86400);
  const h = Math.floor((totalSecs % 86400) / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return { d, h, m, s, finished: diff <= 0 };
}

const DISPLAY_FONT = "'Bebas Neue', 'Oswald', 'Impact', sans-serif";

export function StreamStartingWidget({ fields }: { fields: Record<string, unknown> }) {
  const targetDate = fields.targetDate as string | undefined;
  const title = String(fields.title ?? 'Stream starts in');
  const streamTitle = String(fields.streamTitle ?? 'Welcome back to the stream');
  const startedMessage = String(fields.startedMessage ?? 'Going live now…');
  const accent = String(fields.accentColor ?? '#a855f7');
  const secondary = String(fields.secondaryColor ?? '#06b6d4');
  const backgroundUrl = (fields.backgroundUrl as string | undefined) || null;
  const showSocials = (fields.showSocials as boolean | undefined) ?? true;
  const twitchHandle = String(fields.twitchHandle ?? '').trim();
  const youtubeHandle = String(fields.youtubeHandle ?? '').trim();
  const kickHandle = String(fields.kickHandle ?? '').trim();
  const showScanlines = (fields.showScanlines as boolean | undefined) ?? true;

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const segments = useMemo(() => {
    if (!targetDate) return null;
    const target = new Date(targetDate).getTime();
    if (Number.isNaN(target)) return null;
    return computeSegments(target, now);
  }, [targetDate, now]);

  const finished = segments?.finished ?? false;
  const showDays = segments ? segments.d > 0 : false;

  // Split the title on whitespace so each word stacks on its own line —
  // produces the giant 2-line "STARTING / SOON." vibe by default. Falls
  // back to one line for short titles.
  const titleLines = title.toUpperCase().split(/\s+/).filter(Boolean);

  const socials: Array<{ platform: 'twitch' | 'youtube' | 'kick'; color: string; handle: string }> =
    [];
  if (twitchHandle) socials.push({ platform: 'twitch', color: '#9146ff', handle: twitchHandle });
  if (youtubeHandle) socials.push({ platform: 'youtube', color: '#ff0000', handle: youtubeHandle });
  if (kickHandle) socials.push({ platform: 'kick', color: '#53fc18', handle: kickHandle });

  // Big ghost number behind the layout — counts down with the timer
  // (uses minutes when > 0, otherwise seconds). Just visual flavor.
  const ghostNumber = (() => {
    if (!segments || finished) return null;
    if (segments.d > 0) return String(segments.d);
    const totalMin = segments.h * 60 + segments.m;
    if (totalMin > 0) return String(totalMin);
    return String(segments.s);
  })();

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        fontFamily: "'Inter', 'Poppins', system-ui, sans-serif",
        color: 'white',
        background: backgroundUrl
          ? `#05030f url(${backgroundUrl}) center/cover no-repeat`
          : '#05030f',
      }}
    >
      {/* Animated gradient mesh (only when no custom background) */}
      {!backgroundUrl && (
        <>
          <div
            style={{
              position: 'absolute',
              inset: -200,
              background: `radial-gradient(circle at 20% 30%, ${accent}77, transparent 55%)`,
              filter: 'blur(100px)',
              animation: 'ss-drift-a 18s ease-in-out infinite',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: -200,
              background: `radial-gradient(circle at 80% 70%, ${secondary}66, transparent 55%)`,
              filter: 'blur(100px)',
              animation: 'ss-drift-b 22s ease-in-out infinite',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: -200,
              background: `radial-gradient(circle at 60% 20%, ${accent}33, transparent 60%)`,
              filter: 'blur(140px)',
              animation: 'ss-drift-c 26s ease-in-out infinite',
            }}
          />
        </>
      )}

      {/* Diagonal accent slash crossing right half */}
      <div
        style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: 1100,
          height: 1500,
          background: `linear-gradient(135deg, transparent 45%, ${accent}22 47%, ${accent}55 50%, ${secondary}22 53%, transparent 55%)`,
          transform: 'rotate(12deg)',
          pointerEvents: 'none',
          mixBlendMode: 'screen',
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: '-30%',
          right: '-25%',
          width: 1200,
          height: 1800,
          background: `linear-gradient(135deg, transparent 48%, ${secondary}11 50%, transparent 52%)`,
          transform: 'rotate(12deg)',
          pointerEvents: 'none',
        }}
      />

      {/* Vignette */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at 30% 40%, transparent 30%, rgba(0,0,0,0.7) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Film grain / scanlines */}
      {showScanlines && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 3px)',
            mixBlendMode: 'overlay',
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Ghost number watermark */}
      {ghostNumber && (
        <div
          style={{
            position: 'absolute',
            right: -80,
            bottom: -260,
            fontFamily: DISPLAY_FONT,
            fontSize: 1200,
            fontWeight: 900,
            lineHeight: 0.8,
            color: 'transparent',
            WebkitTextStroke: `2px ${accent}22`,
            letterSpacing: -40,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {ghostNumber}
        </div>
      )}

      {/* Left vertical accent bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          width: 8,
          background: `linear-gradient(180deg, transparent 0%, ${accent} 25%, ${secondary} 75%, transparent 100%)`,
          boxShadow: `0 0 32px ${accent}77`,
        }}
      />

      {/* TOP-LEFT EPIC TEXT BLOCK */}
      <div
        style={{
          position: 'absolute',
          top: 120,
          left: 120,
          right: 120,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0,
        }}
      >
        {/* Eyebrow chip */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 14,
            padding: '10px 22px',
            borderRadius: 4,
            background: 'rgba(0,0,0,0.4)',
            border: `1px solid ${accent}77`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: 6,
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.9)',
            marginBottom: 28,
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 10,
              height: 10,
              borderRadius: 0,
              background: accent,
              boxShadow: `0 0 16px ${accent}, 0 0 32px ${accent}`,
              animation: 'ss-blink 1.4s ease-in-out infinite',
            }}
          />
          {finished ? 'WE ARE LIVE' : 'ON AIR IN'}
        </div>

        {/* Massive title — one word per line. */}
        <div
          style={{
            fontFamily: DISPLAY_FONT,
            fontSize: titleLines.length >= 3 ? 200 : 260,
            fontWeight: 900,
            lineHeight: 0.88,
            letterSpacing: -4,
            color: 'white',
            textShadow: `0 8px 60px rgba(0,0,0,0.6), 0 0 80px ${accent}33`,
            marginBottom: 36,
          }}
        >
          {finished
            ? startedMessage
                .toUpperCase()
                .split(/\s+/)
                .filter(Boolean)
                .map((w, i) => (
                  <div
                    key={`${w}-${i}`}
                    style={{
                      background: `linear-gradient(135deg, ${accent}, ${secondary})`,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      animation: 'ss-finished-pulse 1.6s ease-in-out infinite',
                    }}
                  >
                    {w}
                  </div>
                ))
            : titleLines.map((w, i) => (
                <div
                  key={`${w}-${i}`}
                  style={{ animation: `ss-line-in 700ms ease-out ${i * 90}ms both` }}
                >
                  {w}
                  {i === titleLines.length - 1 && (
                    <span style={{ color: accent, textShadow: `0 0 40px ${accent}` }}>.</span>
                  )}
                </div>
              ))}
        </div>

        {/* Countdown row */}
        {!finished && segments && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 18,
              fontFamily: DISPLAY_FONT,
              fontVariantNumeric: 'tabular-nums',
              marginBottom: 32,
            }}
          >
            {showDays && (
              <>
                <TimeBlock value={pad2(segments.d)} label="DAYS" accent={accent} />
                <Sep />
              </>
            )}
            <TimeBlock value={pad2(segments.h)} label="HRS" accent={accent} />
            <Sep />
            <TimeBlock value={pad2(segments.m)} label="MIN" accent={accent} />
            <Sep />
            <TimeBlock value={pad2(segments.s)} label="SEC" accent={secondary} pulse />
          </div>
        )}

        {/* Stream title pill */}
        {streamTitle && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 16,
              fontSize: 28,
              fontWeight: 500,
              letterSpacing: 0.3,
              color: 'rgba(255,255,255,0.78)',
              maxWidth: 1200,
              lineHeight: 1.25,
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: 48,
                height: 2,
                background: `linear-gradient(90deg, ${accent}, transparent)`,
              }}
            />
            {streamTitle}
          </div>
        )}
      </div>

      {/* Socials strip (bottom-right) */}
      {showSocials && socials.length > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: 80,
            right: 120,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}
        >
          {socials.map((s, i) => (
            <div
              key={s.platform}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 22px',
                borderRadius: 4,
                background: 'rgba(0,0,0,0.55)',
                border: `1px solid ${s.color}77`,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: 1.5,
                color: 'white',
                animation: `ss-social-in 600ms ease-out ${i * 120}ms both`,
                boxShadow: `0 8px 32px rgba(0,0,0,0.4), inset 0 0 24px ${s.color}22`,
              }}
            >
              <PlatformGlyph platform={s.platform} color={s.color} />
              <span style={{ textTransform: 'lowercase' }}>{s.handle}</span>
            </div>
          ))}
        </div>
      )}

      {/* Bottom-left brand mark */}
      <div
        style={{
          position: 'absolute',
          bottom: 32,
          left: 32,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 4,
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.3)',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: 24,
            height: 2,
            background: `linear-gradient(90deg, ${accent}, ${secondary})`,
          }}
        />
        HypeVault
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Oswald:wght@700;800&display=swap');
        @keyframes ss-drift-a {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(160px, -80px) scale(1.15); }
        }
        @keyframes ss-drift-b {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-180px, 120px) scale(1.1); }
        }
        @keyframes ss-drift-c {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(120px, 80px) scale(1.2); }
        }
        @keyframes ss-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes ss-finished-pulse {
          0%, 100% { filter: drop-shadow(0 0 30px ${accent}88); }
          50% { filter: drop-shadow(0 0 70px ${accent}); }
        }
        @keyframes ss-line-in {
          0% { transform: translateX(-40px); opacity: 0; clip-path: inset(0 100% 0 0); }
          100% { transform: translateX(0); opacity: 1; clip-path: inset(0 0 0 0); }
        }
        @keyframes ss-tick {
          0% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          100% { transform: translateY(0); }
        }
        @keyframes ss-social-in {
          0% { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        @keyframes ss-sec-pulse {
          0%, 100% { text-shadow: 0 0 30px ${secondary}66; }
          50% { text-shadow: 0 0 60px ${secondary}; }
        }
      `}</style>
    </div>
  );
}

function TimeBlock({
  value,
  label,
  accent,
  pulse,
}: {
  value: string;
  label: string;
  accent: string;
  pulse?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
      <span
        key={value}
        style={{
          fontSize: 180,
          fontWeight: 900,
          lineHeight: 0.85,
          letterSpacing: -2,
          color: 'white',
          textShadow: `0 0 30px ${accent}55, 0 6px 24px rgba(0,0,0,0.5)`,
          animation: pulse
            ? `ss-tick 380ms cubic-bezier(0.34,1.56,0.64,1), ss-sec-pulse 1.4s ease-in-out infinite`
            : 'ss-tick 380ms cubic-bezier(0.34,1.56,0.64,1)',
          fontFamily: 'inherit',
        }}
      >
        {value}
      </span>
      <span
        style={{
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: 6,
          color: `${accent}cc`,
          paddingLeft: 4,
        }}
      >
        {label}
      </span>
    </div>
  );
}

function Sep() {
  return (
    <span
      style={{
        fontFamily: DISPLAY_FONT,
        fontSize: 160,
        fontWeight: 900,
        lineHeight: 0.85,
        color: 'rgba(255,255,255,0.18)',
        paddingBottom: 20,
      }}
    >
      :
    </span>
  );
}

function PlatformGlyph({
  platform,
  color,
}: {
  platform: 'twitch' | 'youtube' | 'kick';
  color: string;
}) {
  if (platform === 'twitch') {
    return (
      <svg width="22" height="22" viewBox="0 0 24 24" fill={color} aria-hidden>
        <path d="M4 2v18h6v3l3-3h4l6-6V2H4zm17 11l-3 3h-4l-3 3v-3H7V4h14v9zm-5-7h-2v6h2V6zm-5 0H9v6h2V6z" />
      </svg>
    );
  }
  if (platform === 'youtube') {
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill={color} aria-hidden>
        <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.6 3.5 12 3.5 12 3.5s-7.6 0-9.4.6A3 3 0 00.5 6.2C0 8 0 12 0 12s0 4 .5 5.8a3 3 0 002.1 2.1c1.8.6 9.4.6 9.4.6s7.6 0 9.4-.6a3 3 0 002.1-2.1C24 16 24 12 24 12s0-4-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z" />
      </svg>
    );
  }
  // Kick — wedge mark
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill={color} aria-hidden>
      <path d="M3 3h5v5h2V3h5v5h-2v2h-2v4h2v2h2v5h-5v-5h-2v-2H8v7H3V3z" />
    </svg>
  );
}
